import React, { useState, useEffect } from 'react';
import './Header.css';
import { useTranslation } from '../../i18n/TranslationContext';
import type { Language } from '../../i18n/translations';

interface HeaderProps {
  scrolled: boolean;
}

const Header: React.FC<HeaderProps> = ({ scrolled }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, changeLanguage, t } = useTranslation();
  
  // Close mobile menu when clicking outside
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  const switchLanguage = (lang: Language) => {
    changeLanguage(lang);
  };
  
  return (
    <>
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <a href="#accueil">
                <span>Vaudreuil</span>Multiservices
              </a>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="desktop-nav">
              <ul>
                <li><a href="#accueil">{t.header.home}</a></li>
                <li><a href="#about">{t.header.about}</a></li>
                <li><a href="#services">{t.header.services}</a></li>
                <li><a href="#gallery">{t.header.gallery}</a></li>
              </ul>
            </nav>
            
            {/* Desktop Language Switcher & Contact */}
            <div className="header-right">
              <div className="language-switcher">
                <button 
                  className={`lang-btn ${language === 'fr' ? 'active' : ''}`} 
                  onClick={() => switchLanguage('fr')}
                >
                  FR
                </button>
                <span className="lang-divider">|</span>
                <button 
                  className={`lang-btn ${language === 'en' ? 'active' : ''}`} 
                  onClick={() => switchLanguage('en')}
                >
                  EN
                </button>
              </div>
              
              <a href="#contact" className="contact-btn">
                <i className="fas fa-phone"></i>
                <span>{t.header.contactBtn}</span>
              </a>
            </div>
            
            {/* Mobile Menu Toggle */}
            <button 
              className={`mobile-menu-btn ${menuOpen ? 'active' : ''}`}
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>
      
      {/* Mobile Menu (Separate from header) */}
      <div className={`mobile-menu ${menuOpen ? 'active' : ''}`}>
        <div className="mobile-menu-container">
          <div className="mobile-menu-header">
            <div className="logo">
              <a href="#accueil">
                <span>Vaudreuil</span>Multiservices
              </a>
            </div>
            <button className="mobile-close-btn" onClick={toggleMenu}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="mobile-menu-content">
            <ul className="mobile-nav-links">
              <li>
                <a href="#accueil" onClick={toggleMenu}>
                  <i className="fas fa-home"></i>
                  <span>{t.header.home}</span>
                </a>
              </li>
              <li>
                <a href="#about" onClick={toggleMenu}>
                  <i className="fas fa-info-circle"></i>
                  <span>{t.header.about}</span>
                </a>
              </li>
              <li>
                <a href="#services" onClick={toggleMenu}>
                  <i className="fas fa-tools"></i>
                  <span>{t.header.services}</span>
                </a>
              </li>
              <li>
                <a href="#gallery" onClick={toggleMenu}>
                  <i className="fas fa-images"></i>
                  <span>{t.header.gallery}</span>
                </a>
              </li>
              <li>
                <a href="#contact" onClick={toggleMenu}>
                  <i className="fas fa-phone"></i>
                  <span>{t.header.contactBtn}</span>
                </a>
              </li>
            </ul>
            
            <div className="mobile-lang-switcher">
              <button 
                className={`lang-btn ${language === 'fr' ? 'active' : ''}`} 
                onClick={() => switchLanguage('fr')}
              >
                FR
              </button>
              <span className="lang-divider">|</span>
              <button 
                className={`lang-btn ${language === 'en' ? 'active' : ''}`} 
                onClick={() => switchLanguage('en')}
              >
                EN
              </button>
            </div>
          </div>
        </div>
        <div className="mobile-menu-overlay" onClick={toggleMenu}></div>
      </div>
    </>
  );
};

export default Header;